import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, Mail, KeyRound } from 'lucide-react';
import '@/App.css';

export default function AuthPage() {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!isLogin && !accessCode.trim()) {
      setError('Access code is required');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, accessCode);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-bg min-h-screen flex items-center justify-center p-4" data-testid="auth-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <Lock className="w-8 h-8 text-accent-amber" />
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">VaultNotes</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Privacy-first. Markdown-native. Your notes, encrypted.
          </p>
        </div>

        <div className="bg-card border border-border rounded-sm p-6" data-testid="auth-form-container">
          <div className="flex mb-6 border-b border-border">
            <button
              className={`flex-1 pb-3 text-sm font-medium transition-colors ${
                isLogin ? 'text-foreground border-b-2 border-accent-amber' : 'text-muted-foreground'
              }`}
              onClick={() => { setIsLogin(true); setError(''); }}
              data-testid="auth-login-tab"
            >
              Sign In
            </button>
            <button
              className={`flex-1 pb-3 text-sm font-medium transition-colors ${
                !isLogin ? 'text-foreground border-b-2 border-accent-amber' : 'text-muted-foreground'
              }`}
              onClick={() => { setIsLogin(false); setError(''); }}
              data-testid="auth-register-tab"
            >
              Create Account
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-9 bg-secondary/50 border-border"
                  data-testid="auth-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className="pl-9 pr-9 bg-secondary/50 border-border"
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="auth-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-widest text-muted-foreground">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat password"
                    className="pl-9 bg-secondary/50 border-border"
                    data-testid="auth-confirm-password-input"
                  />
                </div>
              </div>
            )}

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="accessCode" className="text-xs uppercase tracking-widest text-muted-foreground">
                  Access Code
                </Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="accessCode"
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    placeholder="Required to create an account"
                    className="pl-9 bg-secondary/50 border-border"
                    data-testid="auth-access-code-input"
                  />
                </div>
              </div>
            )}

            {error && (
              <p className="text-xs text-red-400" data-testid="auth-error-message">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full bg-foreground text-background hover:bg-foreground/90 font-medium"
              disabled={submitting}
              data-testid="auth-submit-btn"
            >
              {submitting ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          <p className="text-xs text-center text-muted-foreground mt-6">
            {isLogin ? 'No account? ' : 'Already have an account? '}
            <button
              className="text-accent-amber hover:underline"
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
              data-testid="auth-switch-mode"
            >
              {isLogin ? 'Create one' : 'Sign in'}
            </button>
          </p>
        </div>

        <p className="text-xs text-center text-muted-foreground/50 mt-6">
          End-to-end encrypted. Self-hostable. Open source.
        </p>
      </div>
    </div>
  );
}
